import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { InternalLinks } from "@/components/shared/InternalLinks";
import { SEOExpansion } from "@/components/shared/SEOExpansion";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { getPageContent, getComingSoonLocations } from "@/lib/api";
import { distinctStateCount } from "@/lib/stateNormalization";
import { AwardsSection } from "@/components/shared/AwardsSection";
import { WhyChoosePmw } from "@/components/shared/WhyChoosePmw";
import {
  Shield, Award, TrendingUp, Headphones, MapPin, CheckCircle,
  ArrowRight, Users, Store, DollarSign, Zap, Globe,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = { Shield, Award, TrendingUp, Headphones, MapPin, DollarSign, Zap, Store, Globe, Users, CheckCircle };

const defaultBenefits = [
  { title: "Trusted Premier Master Agent", description: "Prime Media Wireless is an authorized Trusted Premier Total Wireless Master Agent, backed by Verizon's nationwide 5G network.", icon: "Shield" },
  { title: "No Franchise Fees", description: "Zero franchise fees or mandatory upfront payments. We invest in your success from day one.", icon: "DollarSign" },
  { title: "Store Build-Out Support", description: "We provide store fixtures, interior branding, live demo units, and up to $10,000 signage reimbursement.", icon: "Store" },
  { title: "Sign-Up Bonuses Up to $15,000", description: "Earn $5,000–$15,000 sign-up bonuses in select high-priority markets across the country.", icon: "TrendingUp" },
  { title: "7-Day Support Team", description: "Dedicated area managers and back-office support available 7 days a week to help you succeed.", icon: "Headphones" },
  { title: "$6,000 Advertising Funds", description: "$6,000 in advertising funds per location for mailers, grand openings, and promotional campaigns.", icon: "Zap" },
];

const defaultIncentives = [
  { label: "$10,000", value: "Signage Reimbursement", description: "Per store location" },
  { label: "$15,000", value: "Sign-Up Bonus", description: "In select high-priority markets" },
  { label: "$6,000", value: "Advertising Funds", description: "Per location for marketing" },
];

const defaultProvide = [
  "Store fixtures and interior branding", "Live demo units (fully covered)",
  "$6,000 in advertising funds per location", "Ongoing training and certification",
  "Dedicated area manager support (7 days/week)", "Monthly bonuses for multi-location operators (7+ stores)",
  "Up to $10,000 signage reimbursement", "Grand opening support and promotional materials",
];

const defaultResponsibilities = [
  "Lease signing for retail location", "Store build-out per Verizon guidelines",
  "Equipment (computers, printers, scanners)", "Accessory merchandising",
  "Net 30 payment on initial phone orders; Net 14 thereafter",
  "Signage installation within 30 days (reimbursed up to $10,000)",
];

const defaultFaqs = [
  { title: "What is a Total Wireless Master Agent?", description: "A Total Wireless Master Agent is an authorized partner of Total Wireless (Covered by Verizon) that recruits, trains, and supports independent retailers who open Total Wireless branded retail stores. Prime Media Wireless is a Trusted Premier Total Wireless Master Agent operating nationwide." },
  { title: "How much does it cost to open a Total Wireless store?", description: "There are no franchise fees. You'll need capital for lease signing, store build-out (flooring, paint, partition wall), equipment (computers, printers, scanners), and initial inventory. Prime Media Wireless provides fixtures, branding, demo units, and $6,000 in advertising funds per location." },
  { title: "What areas are available for new Total Wireless stores?", description: "Prime Media Wireless has dealer opportunities in all 50 states. Some high-priority markets offer enhanced sign-up bonuses of up to $15,000. Contact us to learn which territories are available in your area." },
  { title: "What support does Prime Media Wireless provide to dealers?", description: "We provide comprehensive support including store fixtures and branding, live demo units, $6,000 in advertising funds, ongoing training, dedicated area managers, and 7-day-a-week back-office support. Multi-location operators (7+ stores) also receive monthly bonuses." },
  { title: "How is Total Wireless different from other prepaid carriers?", description: "Total Wireless is Covered by Verizon — meaning customers get access to Verizon's award-winning 5G network at no-contract prepaid prices. This gives retailers a strong competitive advantage with a nationally recognized brand backed by the most reliable network in America." },
];

const stateHighlights = [
  "Michigan", "Florida", "Texas", "California", "Georgia",
  "Illinois", "Ohio", "New York", "Pennsylvania", "North Carolina",
];

export default function TotalWirelessMasterAgentPage() {
  const [locations, setLocations] = useState([]);
  const [comingSoonLocations, setComingSoonLocations] = useState([]);
  const [openFaq, setOpenFaq] = useState(null);
  const [cms, setCms] = useState({});

  useEffect(() => {
    fetch(`${API_URL}/api/locations?active_only=true`)
      .then((r) => r.json())
      .then((data) => setLocations(Array.isArray(data) ? data : []))
      .catch(() => {});
    // Coming-soon (pre-approved) applications also count toward our footprint
    // — the /locations directory already surfaces them, but this page was
    // silently under-reporting because the fetch above hits `active_only=True`.
    getComingSoonLocations()
      .then((data) => setComingSoonLocations(Array.isArray(data) ? data : []))
      .catch(() => {});
    getPageContent("twma").then((sections) => {
      const map = {};
      (sections || []).forEach((s) => { map[s.section_name || s.section] = s; });
      setCms(map);
    }).catch(() => {});
  }, []);

  const allFootprint = [...locations, ...comingSoonLocations];
  // Normalize state values (canonicalize "MI" ↔ "Michigan", trim trailing
  // spaces) before counting so DB inconsistencies don't inflate the total.
  const stateCount = distinctStateCount(allFootprint);
  const totalLocationCount = allFootprint.length;

  // CMS-driven content with fallbacks
  const hero = cms.hero || {};
  const heroTitle = hero.title || "Total Wireless\nMaster Agent";
  const heroContent = hero.content || "Prime Media Wireless is a Trusted Premier Total Wireless Master Agent — Covered by Verizon. We partner with motivated entrepreneurs to open exclusive Total Wireless branded retail stores across the United States. No franchise fees. Full support. Real opportunity.";
  const heroSubtitle = hero.metadata?.subtitle || "Trusted Premier Master Agent";
  const heroBtnText = hero.button_text || "Become a Dealer";
  const heroBtnLink = hero.button_link || "/become-dealer";
  const heroBtn2Text = hero.metadata?.secondary_button_text || `View ${totalLocationCount}+ Locations`;
  const heroBtn2Link = hero.metadata?.secondary_button_link || "/locations";

  const aboutSection = cms.about || {};
  const aboutTitle = aboutSection.title || "What is a Total Wireless Master Agent?";
  const aboutContent = aboutSection.content || `<p>A <strong>Total Wireless Master Agent</strong> is an authorized partner of Total Wireless — Covered by Verizon — that recruits, trains, and supports independent wireless retailers across the country.</p><p><strong>Prime Media Wireless</strong> is a Trusted Premier Total Wireless Master Agent with ${totalLocationCount}+ retail locations across ${stateCount} states. We provide everything a dealer needs to succeed.</p><p>Total Wireless, Covered by Verizon, gives customers access to America's most reliable 5G network at affordable no-contract prices.</p>`;

  const benefitsSection = cms.benefits || {};
  const benefitsTitle = benefitsSection.title || "Why Partner with Prime Media Wireless?";
  const benefitsSubtitle = benefitsSection.metadata?.subtitle || "As a Trusted Premier Total Wireless Master Agent, we offer unmatched dealer support and incentives.";
  const benefits = (benefitsSection.metadata?.items?.length > 0) ? benefitsSection.metadata.items : defaultBenefits;

  const incentivesSection = cms.incentives || {};
  const incentivesTitle = incentivesSection.title || "Total Wireless Dealer Incentives";
  const incentives = (incentivesSection.metadata?.items?.length > 0) ? incentivesSection.metadata.items : defaultIncentives;

  const provideSection = cms.what_we_provide || {};
  const provideTitle = provideSection.title || "What We Provide";
  const provideItems = (provideSection.metadata?.items?.length > 0) ? provideSection.metadata.items.map((i) => i.title || i) : defaultProvide;

  const respSection = cms.responsibilities || {};
  const respTitle = respSection.title || "Dealer Responsibilities";
  const respItems = (respSection.metadata?.items?.length > 0) ? respSection.metadata.items.map((i) => i.title || i) : defaultResponsibilities;

  const statesSection = cms.states || {};
  const statesTitle = statesSection.title || "Open a Total Wireless Store in Your State";
  const statesContent = statesSection.content || "Prime Media Wireless has Total Wireless dealer opportunities in all 50 states. Click your state to learn more.";

  const faqsSection = cms.faqs || {};
  const faqsTitle = faqsSection.title || "Frequently Asked Questions";
  const faqs = (faqsSection.metadata?.items?.length > 0) ? faqsSection.metadata.items : defaultFaqs;

  const ctaSection = cms.cta || {};
  const ctaTitle = ctaSection.title || "Ready to Open a Total Wireless Store?";
  const ctaContent = ctaSection.content || "Partner with Prime Media Wireless — a Trusted Premier Total Wireless Master Agent. No franchise fees. Full support. Apply today.";
  const ctaBtnText = ctaSection.button_text || "Apply Now";
  const ctaBtnLink = ctaSection.button_link || "/become-dealer";
  const ctaBtn2Text = ctaSection.metadata?.secondary_button_text || "Contact Us";
  const ctaBtn2Link = ctaSection.metadata?.secondary_button_link || "/contact";

  return (
    <PublicLayout>
      <SEOHead pageSlug="totalwirelessmasteragent" fallback={{
        title: "Total Wireless Master Agent | Prime Media Wireless — Covered by Verizon",
        description: "Prime Media Wireless is a Trusted Premier Total Wireless Master Agent. Open an exclusive Total Wireless store — Covered by Verizon.",
      }} />

      <div data-testid="tw-master-agent-page">
        {/* Hero */}
        <section className="relative bg-navy overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-96 h-96 bg-teal rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-blue-500 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
          </div>
          <div className="max-w-6xl mx-auto px-6 py-20 sm:py-28 relative z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <p className="text-teal font-semibold text-sm tracking-widest uppercase mb-4">{heroSubtitle}</p>
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6">
                {heroTitle.split("\n").map((line, i) => (
                  <span key={i}>{i > 0 && <br />}{i > 0 ? <span className="text-teal">{line}</span> : line}</span>
                ))}
              </h1>
              <p className="text-slate-300 font-body text-lg max-w-2xl mb-8 leading-relaxed">{heroContent}</p>
              <div className="flex flex-wrap gap-4">
                <Link to={heroBtnLink}>
                  <Button size="lg" className="bg-teal hover:bg-teal/90 text-white font-semibold px-8" data-testid="tw-hero-cta">
                    {heroBtnText} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to={heroBtn2Link}>
                  <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8">{heroBtn2Text}</Button>
                </Link>
              </div>
              <div className="flex gap-8 mt-10 text-white/80">
                <div className="flex items-center gap-2"><Store className="w-5 h-5 text-teal" /><span className="text-sm font-body">{totalLocationCount}+ Stores</span></div>
                <div className="flex items-center gap-2"><Globe className="w-5 h-5 text-teal" /><span className="text-sm font-body">{stateCount} States</span></div>
                <div className="flex items-center gap-2"><Users className="w-5 h-5 text-teal" /><span className="text-sm font-body">Nationwide</span></div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Required Qualifications — featured-snippet target. Google extracts
            a clean <ul> at the top of high-authority pages. Mirrors AntGen's
            #2-ranking SERP pattern for "total wireless master agent". */}
        <section className="py-12 sm:py-16 bg-white border-b border-slate-100" data-testid="required-qualifications">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-4">Required Qualifications</h2>
              <p className="text-slate-600 font-body mb-5 text-base leading-relaxed">
                To become an authorized Total Wireless Exclusive Retailer through Prime Media Wireless, you should meet these basic requirements:
              </p>
              <ul className="space-y-2 text-slate-700 font-body text-base list-disc pl-5" data-testid="required-qualifications-list">
                <li>Live in the same market as your planned Total Wireless store(s).</li>
                <li>Minimum 2 years' experience in wireless or general retail.</li>
                <li>Own or lease prime retail real estate (≈400 sq ft+ with high foot traffic).</li>
                <li>Valid business license + EIN for the operating entity.</li>
                <li>Working capital of $15,000–$40,000 to cover inventory, fixtures, and launch marketing.</li>
                <li>Strong customer service and willingness to sell Total Wireless exclusively during the agreement term.</li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* What is a Total Wireless Master Agent */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-6">{aboutTitle}</h2>
              <div className="text-slate-600 font-body space-y-4 text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: aboutContent }} />
            </motion.div>
          </div>
        </section>

        {/* Awards + Why Choose — pillar #1 leads with the 2026 Growth Champion */}
        <AwardsSection compact />
        <WhyChoosePmw />

        {/* Benefits Grid */}
        <section className="py-16 sm:py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-3">{benefitsTitle}</h2>
              <p className="text-slate-500 font-body max-w-xl mx-auto">{benefitsSubtitle}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((b, i) => {
                const Icon = iconMap[b.icon] || Shield;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-lg hover:border-teal/30 transition-all">
                    <div className="w-12 h-12 rounded-xl bg-teal/10 flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-teal" /></div>
                    <h3 className="font-heading font-semibold text-navy mb-2">{b.title}</h3>
                    <p className="text-slate-500 font-body text-sm leading-relaxed">{b.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Incentives */}
        <section className="py-16 sm:py-20 bg-navy text-white">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-10 text-center">{incentivesTitle}</h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {incentives.map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                  className="text-center bg-white/5 backdrop-blur rounded-xl p-8 border border-white/10">
                  <p className="text-4xl font-bold text-teal mb-2">{item.label}</p>
                  <p className="font-heading font-semibold text-lg mb-1">{item.value}</p>
                  <p className="text-white/60 text-sm font-body">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Provide & Responsibilities */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="font-heading font-bold text-xl text-navy mb-6 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-teal" /> {provideTitle}
                </h3>
                <ul className="space-y-3 text-slate-600 font-body text-sm">
                  {provideItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />{item}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="font-heading font-bold text-xl text-navy mb-6 flex items-center gap-2">
                  <Award className="w-5 h-5 text-teal" /> {respTitle}
                </h3>
                <ul className="space-y-3 text-slate-600 font-body text-sm">
                  {respItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2"><ArrowRight className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Available States */}
        <section className="py-16 sm:py-20 bg-slate-50">
          <div className="max-w-5xl mx-auto px-6">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-3 text-center">{statesTitle}</h2>
            <p className="text-slate-500 font-body text-center max-w-xl mx-auto mb-10">{statesContent}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {stateHighlights.map((state) => {
                const slug = state.toLowerCase().replace(/\s+/g, "");
                return (
                  <Link key={slug} to={`/${slug}`} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-body text-navy hover:bg-teal hover:text-white hover:border-teal transition-colors">{state}</Link>
                );
              })}
              <Link to="/open-a-store" className="px-4 py-2 bg-teal text-white rounded-lg text-sm font-body font-semibold hover:bg-teal/90 transition-colors">View All 50 States →</Link>
            </div>
          </div>
        </section>

        {/* FAQs */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-navy mb-10 text-center">{faqsTitle}</h2>
            <div className="space-y-3" data-testid="tw-faqs">
              {faqs.map((faq, i) => (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors">
                    <span className="font-heading font-semibold text-navy text-sm pr-4">{faq.title}</span>
                    <span className={`text-teal text-xl flex-shrink-0 transition-transform ${openFaq === i ? "rotate-45" : ""}`}>+</span>
                  </button>
                  {openFaq === i && (
                    <div className="px-5 pb-5 text-slate-600 font-body text-sm leading-relaxed border-t border-slate-100 pt-4">{faq.description}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 bg-navy text-white text-center">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl mb-4">{ctaTitle}</h2>
            <p className="text-slate-300 font-body mb-8 max-w-lg mx-auto">{ctaContent}</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to={ctaBtnLink}>
                <Button size="lg" className="bg-teal hover:bg-teal/90 text-white font-semibold px-8" data-testid="tw-bottom-cta">{ctaBtnText} <ArrowRight className="w-4 h-4 ml-2" /></Button>
              </Link>
              <Link to={ctaBtn2Link}>
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8">{ctaBtn2Text}</Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
      <SEOExpansion />
      <InternalLinks exclude="/totalwirelessmasteragent" />
      {/* Reciprocal directory links — present in DOM, hidden from view */}
      <div style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0,0,0,0)", whiteSpace: "nowrap", border: 0 }}>
        <a href="https://www.alive2directory.com/">Alive 2 Directory.com</a>
        <a href="https://www.arcticdirectory.com/">Arctic Directory.com</a>
      </div>
    </PublicLayout>
  );
}
